"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import {
	Button,
	HStack,
	IconButton,
	SimpleDialog,
	Stack,
	Text,
} from "@leabankab/component-library";
import {
	NumberField,
	SUBMIT_ERROR_PROPS,
	SelectField,
	focusFirstErrorAfterRender,
} from "@/components/form";
import { Collapse } from "@/components/ui";
import { useCurrency } from "@/hooks";
import { useRevealOnFailedSubmit } from "@/hooks/form/use-reveal-on-failed-submit";
import { isAddedDebtComplete } from "../hooks/use-confirm-debt-form";
import { useDebtTypeOptions } from "../hooks/use-debt-type-options";
import { useSaveAddedDebt } from "../hooks/use-save-added-debt";
import { AddedDebtFormItem } from "../types";
import type { DebtType } from "../utils/debt-types";
import { DebtTypeIcon } from "./debt-type-icon";

interface AddedDebtCardProps {
	index: number;
	allowedTypes: readonly DebtType[];
	defaultExpanded?: boolean;
	/** Bumped when the list validates its unsaved debts. */
	revealAttempt?: number;
	onRemove: () => void;
}

export const AddedDebtCard = ({
	index,
	allowedTypes,
	defaultExpanded = false,
	revealAttempt,
	onRemove,
}: AddedDebtCardProps) => {
	const t = useTranslations("pages.ConfirmDebtPage.addedDebtCard");
	const tCommon = useTranslations("common");
	const { control, setValue, clearErrors, getFieldState } = useFormContext();
	const { format, suffix } = useCurrency();
	const { selectOptions, labelFor, iconFor } = useDebtTypeOptions();

	const namePrefix = `addedDebts.${index}`;
	const contentId = `added-debt-card-${index}-content`;
	const unsavedErrorId = `added-debt-card-${index}-unsaved`;

	const formState = useFormState({ control, name: `${namePrefix}.saved` });
	// Clearing a debt re-validates it, so the message waits for a submit.
	const unsavedError =
		formState.submitCount > 0
			? getFieldState(`${namePrefix}.saved`, formState).error?.message
			: undefined;

	const debt = useWatch({ control, name: namePrefix }) as
		AddedDebtFormItem | undefined;

	const isComplete = isAddedDebtComplete(debt ?? { type: "" }, allowedTypes);
	const saved = (debt?.saved ?? false) && isComplete;
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [confirmOpen, setConfirmOpen] = useState(false);

	useRevealOnFailedSubmit(namePrefix, () => setExpanded(true), revealAttempt);

	// The list saves a finished debt when another is added, and it closes too.
	const savedFlag = debt?.saved ?? false;
	const wasSaved = useRef(savedFlag);
	useEffect(() => {
		if (savedFlag && !wasSaved.current) setExpanded(false);
		wasSaved.current = savedFlag;
	}, [savedFlag]);

	const saveDebt = useSaveAddedDebt();

	const cardRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLButtonElement>(null);
	const deleteRef = useRef<HTMLButtonElement>(null);
	// Indices shift on removal, so a mounted card must not focus itself again.
	const focusOnMount = useRef(defaultExpanded);

	useEffect(() => {
		if (focusOnMount.current) {
			headerRef.current?.focus();
		}
	}, []);

	// `Collapse` unmounts on exit, taking the focused Save or Clear button with it.
	const collapse = () => {
		headerRef.current?.focus();
		setExpanded(false);
	};

	const handleSave = async () => {
		if (!(await saveDebt(index))) {
			focusFirstErrorAfterRender(cardRef);
			return;
		}
		collapse();
	};

	const handleClear = () => {
		setValue(`${namePrefix}.type`, "", { shouldDirty: true });
		setValue(`${namePrefix}.amount`, undefined, { shouldDirty: true });
		setValue(`${namePrefix}.saved`, false, {
			shouldDirty: true,
			shouldValidate: true,
		});
		clearErrors([`${namePrefix}.type`, `${namePrefix}.amount`]);
		collapse();
	};

	const dismissDialog = () => {
		setConfirmOpen(false);
		deleteRef.current?.focus();
	};

	const typeLabel = labelFor(debt?.type);
	const TypeIcon = iconFor(debt?.type);

	return (
		<>
			<div
				ref={cardRef}
				className={`relative rounded-xl border ${
					unsavedError
						? "border-status-error-base"
						: saved
							? "border-purple-400"
							: "border-border-subtle"
				}`}
			>
				<HStack align="center" gap={1} className="py-1.5 ps-1.5 pe-2">
					<button
						ref={headerRef}
						type="button"
						aria-expanded={expanded}
						aria-controls={contentId}
						aria-describedby={unsavedError ? unsavedErrorId : undefined}
						onClick={() => setExpanded((value) => !value)}
						className="flex w-full grow cursor-pointer items-center justify-between gap-1.5 border-0 bg-transparent p-0 text-left"
					>
						{TypeIcon && <DebtTypeIcon icon={TypeIcon} />}

						<Stack className="flex-1">
							<Text fontWeight="semibold" color="text.base">
								{typeLabel || t("title")}
							</Text>
							{debt?.amount != null && (
								<Text textStyle="body-md" color="text.base">
									{format(debt.amount)}
								</Text>
							)}
						</Stack>

						{expanded ? (
							<ChevronUp size={20} aria-hidden="true" />
						) : (
							<ChevronDown size={20} aria-hidden="true" />
						)}
					</button>

					<IconButton
						ref={deleteRef}
						aria-label={t("deleteButton")}
						size="sm"
						variant="ghost"
						onClick={() => setConfirmOpen(true)}
					>
						<Trash2 size={18} aria-hidden="true" />
					</IconButton>
				</HStack>

				<Collapse open={expanded} id={contentId}>
					<Stack gap={2} className="px-2 pb-2">
						<SelectField
							name={`${namePrefix}.type`}
							label={t("fields.type")}
							options={selectOptions}
						/>
						<NumberField
							name={`${namePrefix}.amount`}
							label={t("fields.amount")}
							unit={suffix}
							showSteppers={false}
						/>
						<HStack gap={1} justify="end">
							<Button variant="outline" onClick={handleClear}>
								{tCommon("clear")}
							</Button>
							<Button onClick={handleSave}>{tCommon("save")}</Button>
						</HStack>
					</Stack>
				</Collapse>

				{unsavedError && (
					<Text
						{...SUBMIT_ERROR_PROPS}
						id={unsavedErrorId}
						textStyle="body-sm"
						color="status.error.base"
						className="px-2 pb-1.5"
					>
						{unsavedError}
					</Text>
				)}
			</div>

			<SimpleDialog
				open={confirmOpen}
				onOpenChange={(open) => {
					if (!open) dismissDialog();
				}}
				title={t("deleteModal.header")}
				description={t("deleteModal.body")}
				closeLabel={t("deleteModal.cancel")}
			>
				<HStack gap={1} justify="end">
					<Button variant="outline" onClick={dismissDialog} size="sm">
						{t("deleteModal.cancel")}
					</Button>
					<Button
						onClick={() => {
							setConfirmOpen(false);
							onRemove();
						}}
						size="sm"
					>
						{t("deleteModal.confirm")}
					</Button>
				</HStack>
			</SimpleDialog>
		</>
	);
};
